import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { CONTACT_INFO, BRAND_INFO, LAB_PANELS } from '../data/mock';

const SubscriptionPage = () => {
  usePageTitle(`Subscription Terms | ${BRAND_INFO.name}`,
    `${BRAND_INFO.name} subscription disclosure — recurring-panel cadence, renewal terms, billing descriptor, currency, and cancellation rights.`);

  // Show the recurring-panel-eligible items as the disclosure table
  const recurring = LAB_PANELS.slice(0, 6);

  return (
    <>
      <Header />
      <main className="pt-28 lg:pt-36 pb-24 bg-cream-100">
        <div className="container-page">
          <p className="eyebrow">Legal</p>
          <h1 className="mt-4 font-display text-display-lg text-ink-900">Subscription Terms &amp; Disclosure</h1>
          <p className="mt-4 text-ink-600">Last updated: July 2025</p>

          <p className="mt-8 text-ink-700 leading-relaxed">
            This page explains, in plain language, exactly how the {BRAND_INFO.name} recurring-panel option works. It supplements our <Link to="/terms" className="underline text-brick-500">Terms &amp; Conditions</Link>. {CONTACT_INFO.corporateName} provides this disclosure as part of our card-brand compliance commitments and to help you make an informed decision before you opt in to recurring billing.
          </p>

          <section className="mt-12">
            <h2 className="font-display text-2xl lg:text-3xl text-ink-900">Recurring panels at a glance</h2>
            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-left border-collapse text-[15px]">
                <thead>
                  <tr className="text-xs uppercase tracking-[0.18em] text-ink-500">
                    <th className="py-3 px-4 border-b border-cream-300">Panel</th>
                    <th className="py-3 px-4 border-b border-cream-300">Price (USD)</th>
                    <th className="py-3 px-4 border-b border-cream-300">Cadence</th>
                    <th className="py-3 px-4 border-b border-cream-300">Auto-renews?</th>
                  </tr>
                </thead>
                <tbody className="text-ink-700">
                  {recurring.map((p) => (
                    <tr key={p.id} className="hover:bg-cream-50">
                      <td className="py-3 px-4 border-b border-cream-200 font-medium text-ink-900">{p.name}</td>
                      <td className="py-3 px-4 border-b border-cream-200">${p.price}</td>
                      <td className="py-3 px-4 border-b border-cream-200">Every 90 days (default)</td>
                      <td className="py-3 px-4 border-b border-cream-200">Yes — until cancelled</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-ink-500">All amounts in U.S. Dollars (USD). Applicable taxes will be displayed at checkout where required. Cadence is configurable inside your account between 60, 90, or 180 days.</p>
          </section>

          <section className="mt-12 space-y-6 text-ink-700 leading-relaxed">
            <div>
              <h3 className="font-display text-xl text-ink-900">1. What you get during the cadence</h3>
              <p className="mt-2">Each cadence triggers a fresh physician-signed requisition for the panel you selected, routed to the partner lab of your choice. Markers from each successive draw populate the same dashboard so your timeline keeps growing.</p>
            </div>
            <div>
              <h3 className="font-display text-xl text-ink-900">2. Initial charge</h3>
              <p className="mt-2">Your first charge is processed immediately after you confirm a recurring panel. The exact amount and currency are shown on the order-confirmation screen before you click the final purchase button.</p>
            </div>
            <div>
              <h3 className="font-display text-xl text-ink-900">3. Auto-renewal</h3>
              <p className="mt-2">Your recurring panel renews automatically at the end of each selected cadence at the then-current price for that panel, until you cancel. Your renewal date and price are visible from inside your account at any time. We send a reminder email at least seven (7) days before each renewal.</p>
            </div>
            <div>
              <h3 className="font-display text-xl text-ink-900">4. Billing descriptor</h3>
              <p className="mt-2">Charges will appear on your card or bank statement under the descriptor <span className="font-mono text-brick-600">{BRAND_INFO.billingDescriptor}</span>. If you do not recognize a charge, please contact us before initiating a chargeback so we can help you identify it.</p>
            </div>
            <div>
              <h3 className="font-display text-xl text-ink-900">5. How to cancel</h3>
              <p className="mt-2">You may cancel at any time, with no penalty, by:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Opening Account &rarr; Billing &rarr; Cancel inside the app;</li>
                <li>Emailing {CONTACT_INFO.email};</li>
                <li>Calling {CONTACT_INFO.phone} during business hours;</li>
                <li>Mailing a written cancellation request to {CONTACT_INFO.address.street}, {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}.</li>
              </ul>
              <p className="mt-2">Cancellation prevents future renewals. Your dashboard and existing timeline remain available.</p>
            </div>
            <div>
              <h3 className="font-display text-xl text-ink-900">6. Refund eligibility</h3>
              <p className="mt-2">Refunds are governed by our <Link to="/refund" className="underline text-brick-500">Refund &amp; Cancellation Policy</Link>. In short: a full refund of your most recent charge is available within thirty (30) days, provided that draw has not yet been completed.</p>
            </div>
            <div>
              <h3 className="font-display text-xl text-ink-900">7. Price changes</h3>
              <p className="mt-2">If we change the renewal price of a panel you are currently subscribed to, we will provide notice by email at least thirty (30) days before the change takes effect. You may cancel during that window to avoid the new price.</p>
            </div>
            <div>
              <h3 className="font-display text-xl text-ink-900">8. Country of origin &amp; accepted payment</h3>
              <p className="mt-2">{CONTACT_INFO.corporateName} is based in the United States. Country of origin: United States. We accept Visa, Mastercard, American Express, and Discover. Payments are encrypted in transit using industry-standard TLS.</p>
            </div>
            <div>
              <h3 className="font-display text-xl text-ink-900">9. Questions?</h3>
              <p className="mt-2">Email {CONTACT_INFO.email} or call {CONTACT_INFO.phone}. We answer every message.</p>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default SubscriptionPage;
